import type { AuditException } from '@/types/asset-audit';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Export utility functions for CSV, Excel, and PDF formats

export interface ExportColumn<T = any> {
  key: keyof T;
  label: string;
  format?: (value: any) => any;
}

/**
 * Format a date for export
 */
export function formatDateForExport(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format currency for export
 */
export function formatCurrencyForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.toFixed(2);
}

/**
 * Generate a filename with timestamp
 */
export function generateFilename(prefix: string, extension: string = "csv"): string {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
  return `${prefix}_${timestamp}.${extension}`;
}

/**
 * Escape CSV field value
 */
function escapeCSVField(value: any): string {
  if (value === null || value === undefined) return "";
  
  const stringValue = String(value);
  
  // If the value contains comma, newline, or double quote, wrap it in quotes
  if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * Export data to CSV format (generic)
 */
export function exportToCSV<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
}): void {
  const { data, columns, filename } = options;
  
  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }
    
    // Create CSV header
    const headers = columns.map(col => escapeCSVField(col.label)).join(",");
    
    // Create CSV rows
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        return escapeCSVField(formattedValue);
      }).join(",")
    );
    
    // Combine header and rows
    const csv = [headers, ...rows].join("\n");
    
    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    throw error;
  }
}

/**
 * Export data to Excel format (XLSX)
 * Creates a proper XLSX file using SheetJS library
 */
export function exportToExcel<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
  sheetName?: string;
}): void {
  const { data, columns, filename, sheetName = 'Sheet1' } = options;
  
  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }
    
    // Ensure filename has .xlsx extension
    const xlsxFilename = filename.replace(/\.(xlsx?|csv)$/i, '') + '.xlsx';
    
    // Create header row
    const headers = columns.map(col => col.label);
    
    // Create data rows with formatted values
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        return formattedValue ?? "";
      })
    );
    
    // Combine headers and data
    const worksheetData = [headers, ...rows];
    
    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Auto-size columns based on content
    const colWidths = columns.map((col, colIndex) => {
      const maxLength = Math.max(
        col.label.length,
        ...rows.map(row => String(row[colIndex] ?? "").length)
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
    worksheet['!cols'] = colWidths;
    
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Generate XLSX file and trigger download
    XLSX.writeFile(workbook, xlsxFilename);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw error;
  }
}

/**
 * Export exceptions to CSV format (specific for exceptions)
 */
export const exportExceptionsToCSV = (exceptions: AuditException[], filename: string = 'exceptions.csv') => {
  // Define CSV headers
  const headers = [
    'Exception ID',
    'Type',
    'Severity',
    'Priority',
    'Status',
    'Asset Code',
    'Asset Name',
    'Audit Code',
    'Expected Value',
    'Actual Value',
    'Description',
    'Location',
    'Reported By',
    'Reported Date',
    'Assigned To',
    'Requires Follow-up',
    'Follow-up Date',
    'Has Images',
  ];

  // Convert exceptions to CSV rows
  const rows = exceptions.map((exception) => [
    exception.id,
    exception.exceptionType,
    exception.severity,
    exception.priority,
    exception.status,
    exception.assetCode,
    exception.assetName,
    exception.auditNumber,
    exception.expectedValue ?? '',
    exception.actualValue ?? '',
    `"${(exception.description ?? '').replace(/"/g, '""')}"`, // Escape quotes
    exception.location ?? '',
    exception.reportedByName ?? '',
    format(new Date(exception.reportedDate), 'yyyy-MM-dd HH:mm:ss'),
    exception.assignedToName ?? '',
    exception.requiresFollowUp ? 'Yes' : 'No',
    exception.followUpDueDate ? format(new Date(exception.followUpDueDate), 'yyyy-MM-dd') : '',
    exception.images && exception.images.length > 0 ? 'Yes' : 'No',
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  // Create and download file
  downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
};

/**
 * Export exceptions to Excel format (XLSX)
 */
export const exportExceptionsToExcel = (exceptions: AuditException[], filename: string = 'exceptions.xlsx') => {
  // Define columns for export
  const columns: ExportColumn<AuditException>[] = [
    { key: 'id', label: 'Exception ID' },
    { key: 'exceptionType', label: 'Type' },
    { key: 'severity', label: 'Severity' },
    { key: 'priority', label: 'Priority' },
    { key: 'status', label: 'Status' },
    { key: 'assetCode', label: 'Asset Code' },
    { key: 'assetName', label: 'Asset Name' },
    { key: 'auditNumber', label: 'Audit Code' },
    { key: 'expectedValue', label: 'Expected Value', format: (v) => v ?? '' },
    { key: 'actualValue', label: 'Actual Value', format: (v) => v ?? '' },
    { key: 'description', label: 'Description', format: (v) => v ?? '' },
    { key: 'location', label: 'Location', format: (v) => v ?? '' },
    { key: 'reportedByName', label: 'Reported By', format: (v) => v ?? '' },
    { key: 'reportedDate', label: 'Reported Date', format: (v) => format(new Date(v), 'yyyy-MM-dd HH:mm:ss') },
    { key: 'assignedToName', label: 'Assigned To', format: (v) => v ?? '' },
    { key: 'requiresFollowUp', label: 'Requires Follow-up', format: (v) => v ? 'Yes' : 'No' },
    { key: 'followUpDueDate', label: 'Follow-up Date', format: (v) => v ? format(new Date(v), 'yyyy-MM-dd') : '' },
  ];
  
  exportToExcel({ data: exceptions, columns, filename });
};

/**
 * PDF Export Options
 */
export interface PDFExportOptions<T = any> {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
  title: string;
  subtitle?: string;
  organisationName?: string;
  orientation?: 'portrait' | 'landscape' | 'auto';
  pageSize?: 'a4' | 'letter';
  filters?: Record<string, string>;
  showFilters?: boolean;
  headerColor?: [number, number, number];
  accentColor?: [number, number, number];
}

/**
 * Export data to PDF format with professional formatting
 * Creates a world-class PDF with proper headings, styling, and pagination
 */
export function exportToPDF<T extends Record<string, any>>(options: PDFExportOptions<T>): void {
  const {
    data,
    columns,
    filename,
    title,
    subtitle,
    organisationName,
    orientation = 'auto',
    pageSize = 'a4',
    filters,
    showFilters = true,
    headerColor = [31, 41, 55],
    accentColor = [0, 115, 115], // Primary green #007373
  } = options;

  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }

    // Ensure filename has .pdf extension
    const pdfFilename = filename.replace(/\.(pdf|xlsx?|csv)$/i, '') + '.pdf';

    // Auto-detect orientation based on number of columns
    let finalOrientation: 'portrait' | 'landscape' = 'portrait';
    if (orientation === 'auto') {
      finalOrientation = columns.length > 6 ? 'landscape' : 'portrait';
    } else {
      finalOrientation = orientation;
    }

    // Create PDF document
    const doc = new jsPDF({
      orientation: finalOrientation,
      unit: 'mm',
      format: pageSize,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    // Header section with accent bar
    doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.rect(0, 0, pageWidth, 3, 'F');

    // Title on left
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2]);
    doc.text(title, margin, 18);

    // Generated date/time on right (top right)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    const generatedText = format(new Date(), 'dd MMMM yyyy \'at\' HH:mm');
    doc.text(generatedText, pageWidth - margin, 14, { align: 'right' });
    
    // Total records underneath the date (top right)
    const recordCountText = `${data.length.toLocaleString()} Records`;
    doc.text(recordCountText, pageWidth - margin, 19, { align: 'right' });

    // Organisation name as subtitle (if provided)
    let yPosition = 25;
    if (organisationName) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(organisationName, margin, yPosition);
      yPosition += 7;
    }

    // Additional subtitle if provided (e.g., report-specific info)
    if (subtitle) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(subtitle, margin, yPosition);
      yPosition += 6;
    }

    yPosition += 2;

    // Filters section if provided
    if (showFilters && filters && Object.keys(filters).length > 0) {
      const activeFilters = Object.entries(filters).filter(([, value]) => value && value.trim() !== '');
      
      if (activeFilters.length > 0) {
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(margin, yPosition, pageWidth - (margin * 2), 8 + (activeFilters.length * 5), 2, 2, 'F');
        
        yPosition += 5;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(55, 65, 81);
        doc.text('Applied Filters:', margin + 3, yPosition);
        yPosition += 5;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(75, 85, 99);
        activeFilters.forEach(([key, value]) => {
          const filterLabel = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          doc.text(`${filterLabel}: ${value}`, margin + 6, yPosition);
          yPosition += 4;
        });
        yPosition += 5;
      }
    }

    yPosition += 3;

    // Prepare table headers
    const headers = columns.map(col => col.label);

    // Prepare table data with formatted values
    const tableData = data.map(row =>
      columns.map(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        return formattedValue ?? '';
      })
    );

    // Calculate optimal column widths
    const availableWidth = pageWidth - (margin * 2);
    const minColWidth = 20;
    const maxColWidth = finalOrientation === 'landscape' ? 60 : 45;

    // Calculate content-based widths
    const colWidths = columns.map((col, colIndex) => {
      const headerLength = col.label.length;
      const maxDataLength = Math.max(
        ...tableData.map(row => String(row[colIndex] ?? '').length),
        0
      );
      const charWidth = 2.2;
      const calculatedWidth = Math.max(headerLength, maxDataLength) * charWidth;
      return Math.max(minColWidth, Math.min(calculatedWidth, maxColWidth));
    });

    // Normalize widths to fit page
    const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);
    const scaleFactor = totalWidth > availableWidth ? availableWidth / totalWidth : 1;
    const finalColWidths = colWidths.map(w => w * scaleFactor);

    // Create table with professional styling
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: yPosition,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [229, 231, 235],
        lineWidth: 0.1,
        overflow: 'linebreak',
        cellWidth: 'wrap',
      },
      headStyles: {
        fillColor: headerColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'left',
        fontSize: 9,
        cellPadding: 4,
      },
      bodyStyles: {
        textColor: [55, 65, 81],
        halign: 'left',
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      columnStyles: finalColWidths.reduce((acc, width, index) => {
        acc[index] = { cellWidth: width };
        return acc;
      }, {} as Record<number, { cellWidth: number }>),
      willDrawCell: (data) => {
        // Set dark border color for header cells
        if (data.section === 'head') {
          doc.setDrawColor(20, 20, 20);
          doc.setLineWidth(0.3);
        }
      },
      didDrawPage: (pageData) => {
        // Header accent bar on each page
        doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.rect(0, 0, pageWidth, 3, 'F');

        // Footer with page numbers and branding
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(156, 163, 175);

        // Page number centered
        doc.text(
          `Page ${pageData.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );

        // Report title on left
        doc.text(title, margin, pageHeight - 8);

        // Date on right
        doc.text(
          format(new Date(), 'dd/MM/yyyy'),
          pageWidth - margin,
          pageHeight - 8,
          { align: 'right' }
        );

        // Footer line
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.3);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      },
    });

    // Summary section at the end
    const finalY = (doc as any).lastAutoTable?.finalY || yPosition + 10;
    
    if (finalY < pageHeight - 40) {
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(margin, finalY + 5, pageWidth - (margin * 2), 15, 2, 2, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 65, 81);
      doc.text('Summary', margin + 5, finalY + 13);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Total records exported: ${data.length.toLocaleString()}`, margin + 5, finalY + 18);
      
      const exportedText = `Exported on ${format(new Date(), 'EEEE, dd MMMM yyyy \'at\' HH:mm:ss')}`;
      doc.text(exportedText, pageWidth - margin - 5, finalY + 18, { align: 'right' });
    }

    // Save the PDF
    doc.save(pdfFilename);
  } catch (error) {
    console.error("Error exporting to PDF:", error);
    throw error;
  }
}

/**
 * Export exceptions to PDF format using the professional PDF export
 */
export const exportExceptionsToPDF = (exceptions: AuditException[], filename: string = 'exceptions.pdf') => {
  const columns: ExportColumn<AuditException>[] = [
    { key: 'id', label: 'ID' },
    { key: 'exceptionType', label: 'Type' },
    { key: 'severity', label: 'Severity' },
    { key: 'status', label: 'Status' },
    { key: 'assetCode', label: 'Asset Code' },
    { key: 'assetName', label: 'Asset Name' },
    { key: 'description', label: 'Description', format: (v) => v ?? '' },
    { key: 'reportedByName', label: 'Reported By', format: (v) => v ?? '' },
    { key: 'reportedDate', label: 'Reported Date', format: (v) => format(new Date(v), 'dd/MM/yyyy') },
  ];

  exportToPDF({
    data: exceptions,
    columns,
    filename,
    title: 'Audit Exceptions Report',
    subtitle: 'Asset Management System',
    orientation: 'landscape',
  });
};

/**
 * Helper function to download a file
 */
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get export filename with timestamp
 */
export const getExportFilename = (prefix: string = 'exceptions', extension: string = 'csv'): string => {
  const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
  return `${prefix}_${timestamp}.${extension}`;
};
